import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectFlatsTableDataByEntranceName } from '../../../../../../lib/storeSelectors';
import { useSearch } from './lib/search';
import { useFlatsTableList } from '../../../../../../providers/flatsTableListProvider';
import { Context } from './contexts';
import { createSortedData, filterData, filterDataBySearch } from './lib/data';
import { createFiltersData, getAvailableFilters } from './lib/filter';
import { sortableColumnsData } from '../../../../../../../../components/ui/tables/constants/sortableColumnsData';

const emptySet = new Set();
const emptyObj = {};

const Provider = ({ entranceName, children }) => {
  const dataSourceSelector = useCallback(
    (state) => selectFlatsTableDataByEntranceName(state, entranceName),
    [entranceName]
  );

  const dataSource = useSelector(dataSourceSelector);

  const search = useSearch();

  const { filteredInfo: allFilteredInfo, sortedInfo } = useFlatsTableList();

  const filteredInfo = allFilteredInfo[entranceName];
  const allDataSource = dataSource?.['Все'];

  const filteredData = useMemo(
    () => (dataSource ? filterData({ dataSource, filteredInfo }) : emptySet),
    [dataSource, filteredInfo]
  );

  const availableFilters = useMemo(
    () =>
      allDataSource ? getAvailableFilters({ data: allDataSource }) : emptyObj,
    [allDataSource]
  );

  const filteredBySearchData = useMemo(
    () =>
      search
        ? filterDataBySearch({ data: filteredData, search })
        : Array.from(filteredData),
    [search, filteredData]
  );

  const sortedData = useMemo(() => {
    const { columnKey, order } = sortedInfo || {};

    if (!columnKey || !order) return filteredBySearchData;

    return createSortedData({
      data: filteredBySearchData,
      sortedInfo,
      columnKeys: sortableColumnsData,
    });
  }, [sortedInfo, filteredBySearchData]);

  const filters = useMemo(
    () => createFiltersData({ data: filteredBySearchData, availableFilters }),
    [filteredBySearchData, availableFilters]
  );

  const value = useMemo(
    () => ({
      data: sortedData,
      filters,
      filteredInfo,
      entranceName,
      sortedInfo,
    }),
    [sortedData, filters, filteredInfo, entranceName, sortedInfo]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export default Provider;
